import { Ikon } from '@studio/design-system/button';
import { Column } from '@studio/design-system/form';
import { Motions } from '@studio/design-system/motion';
import { Reorder, useDragging, useOrderedIds } from '@studio/design-system/motion/reorder';
import type { Preset as M_Preset } from '@studio/models/video/preset';
import { useLatestFn, useLazyRef } from '@studio/react-utils/hooks';
import { isNotEmpty } from '@tryiris-ai/js-types';
import { dequal } from 'dequal/lite';
import type { ReactElement } from 'react';
import { memo } from 'react';
import { twMerge } from 'cn';
import { Preset } from './preset/index.js';
import type { ListProps } from './types.js';
import { compareIds } from '#~/models/index.js';
import { Placeholder } from '#~/video/image/preset/components/card/index.js';

/** @internal */
const Inner = ({ className, handler, presets, selectedIds, ...props }: ListProps): ReactElement => {
  // Drag/Drop
  const [orderedIds, setOrderedIds] = useOrderedIds(presets.map((µ) => µ.id));
  const [isDragging, setDragging] = useDragging();

  const handleDragStart = useLatestFn(() => {
    setDragging(true);
  });

  const handleDragEnd = useLatestFn(() => {
    setDragging(false);

    // Has the order actually changed?
    if (!compareIds(presets, orderedIds)) {
      handler.onReorder(orderedIds);
    }
  });

  const handleSelect = useLatestFn((presetId: M_Preset['id']) => {
    // Ignore click events while dragging.
    if (!isDragging) {
      handler.onSelect(presetId);
    }
  });

  // Override `onSelect` handler.
  const passthru = useLazyRef(() => ({
    ...handler,
    onSelect: handleSelect
  })).current;

  return (
    <Column className={twMerge('p-16', className)} {...props}>
      {isNotEmpty(presets) ? (
        <Reorder.Group
          axis='y'
          className={twMerge('flex flex-col gap-16')}
          layoutScroll
          onReorder={setOrderedIds}
          values={orderedIds}
        >
          {orderedIds.map((µ) => {
            const preset = presets.find((p) => p.id === µ);

            // Sanity check
            if (!preset) {
              return null;
            }

            return (
              <Reorder.Item
                key={preset.id}
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
                value={preset.id}
                {...Motions.Drag}
                {...Motions.Fade}
              >
                {/* Be very careful w/ memoization here! */}
                <Preset
                  handler={passthru}
                  isSelected={selectedIds.includes(preset.id)}
                  preset={preset}
                />
              </Reorder.Item>
            );
          })}
        </Reorder.Group>
      ) : (
        <Placeholder onClick={handler.onAdd} />
      )}

      <Ikon className='w-32 self-center text-lg' icon='plus' onClick={handler.onAdd} />
    </Column>
  );
};

/** Image settings preset `<List />` component. */
export const List = memo(Inner, dequal);
